<template>
  <div
    v-if="days.length > 0"
    class="sticky-day-nav"
  >
    <div class="day-nav-container">
      <div
        v-for="(day, index) in days"
        :key="index"
        @click="scrollToRideDay(index)"
        :class="['day-nav-item', { 'day-nav-item-active': isActiveDay(index) }]"
      >
        {{ dayLabel(index) }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";
import { rideDayDate } from "@/utils/rideDayLabels";
import { scrollToRideDay } from "@/utils/scrollToRideElement";

/**
 * The fixed bar of days at the bottom of the trip page.
 *
 * It owns --floating-stack-bottom-extra, which lifts the floating buttons
 * (map link, WhatsApp, AI chat) above it. One writer only: see the note on
 * registerFloatingItem in SingleRideWithMap.vue.
 */
export default {
  name: 'RideDayNav',
  mixins: [translationMixin],
  computed: {
    ...mapGetters(['allRide', 'rideFocusDay']),
    days() {
      return (this.allRide && this.allRide.attractionsByDay) || [];
    },
  },
  mounted() {
    this.updateFloatingStackBottomOffset();
  },
  beforeDestroy() {
    document.documentElement.style.setProperty("--floating-stack-bottom-extra", "0px");
  },
  watch: {
    allRide() {
      this.updateFloatingStackBottomOffset();
    },
    rideFocusDay() {
      // Auto-scroll the active day into view in the navigation bar
      this.$nextTick(() => {
        const activeButton = this.$el && this.$el.querySelector
          ? this.$el.querySelector('.day-nav-item-active')
          : null;
        if (activeButton) {
          activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    },
  },
  methods: {
    scrollToRideDay,
    updateFloatingStackBottomOffset() {
      this.$nextTick(() => {
        const offset = this.$el && this.$el.offsetHeight ? this.$el.offsetHeight : 0;
        document.documentElement.style.setProperty(
          "--floating-stack-bottom-extra",
          `${offset}px`
        );
      });
    },
    isActiveDay(index) {
      // rideFocusDay is 1-based (day 1, 2, 3...) or 0 for no day selected
      return this.rideFocusDay === index + 1 || (this.rideFocusDay === 0 && index === 0);
    },
    dayLabel(index) {
      const ride = this.allRide && this.allRide.ride;
      return rideDayDate(ride, index) || this.t('dayNumber', { number: index + 1 });
    },
  },
};
</script>

<style scoped>
.sticky-day-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: var(--primary-background);
  padding: 12px 0;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
}

.sticky-day-nav::-webkit-scrollbar {
  height: 4px;
}

.sticky-day-nav::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.sticky-day-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 2px;
}

.day-nav-container {
  display: flex;
  gap: 10px;
  padding: 0 20px;
  min-width: min-content;
}

.day-nav-item {
  background: white;
  color: var(--primary-color);
  padding: 10px 20px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 15px;
  white-space: nowrap;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.day-nav-item-active {
  background: var(--primary-background);
  color: var(--primary-text-color);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  font-weight: 700;
}

.day-nav-item:hover:not(.day-nav-item-active) {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  color: #0D2F29;
}

.day-nav-item:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .sticky-day-nav {
    padding: 14px 0;
    padding-bottom: calc(14px + env(safe-area-inset-bottom));
    min-height: 60px;
  }

  .day-nav-container {
    padding: 0 15px;
    gap: 10px;
    min-height: 44px;
    align-items: center;
  }

  .day-nav-item {
    padding: 10px 18px;
    font-size: 15px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
